import React, { useEffect, useState } from "react";
import { X, Mail, Loader2, UserPlus, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";

/**
 * BPMInviteAppManagerModal — modal that lets a Business Process Manager
 * invite an Application Manager for an app they have a process dependency on.
 *
 * Backed by:
 *   GET  /api/bpm/invitable-applications
 *   POST /api/bpm/invite-app-manager
 *
 * Render this from any BPM-facing page (e.g. BIA edit, Process detail).
 * Trigger: <button onClick={() => setShow(true)}>Invite App Manager</button>
 */
export default function BPMInviteAppManagerModal({ open, onClose, onInvited }) {
  const { api } = useAuth();
  const [apps, setApps] = useState([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    application_id: "",
    app_manager_email: "",
    app_manager_first_name: "",
    app_manager_last_name: "",
    message: "",
  });

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const res = await api.get("/bpm/invitable-applications");
        if (!cancelled) setApps(res.data?.applications || []);
      } catch (e) {
        if (!cancelled) toast.error(e?.response?.data?.detail || "Could not load eligible applications");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [open, api]);

  const reset = () => {
    setForm({
      application_id: "",
      app_manager_email: "",
      app_manager_first_name: "",
      app_manager_last_name: "",
      message: "",
    });
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.application_id || !form.app_manager_email || !form.app_manager_first_name) {
      toast.error("Please fill in all required fields");
      return;
    }
    setSubmitting(true);
    try {
      const res = await api.post("/bpm/invite-app-manager", form);
      toast.success(`Invitation sent to ${form.app_manager_email}`);
      reset();
      onClose?.();
      onInvited?.(res.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to send invitation");
    } finally {
      setSubmitting(false);
    }
  };

  if (!open) return null;

  const selectedApp = apps.find((a) => a.id === form.application_id);

  return (
    <div className="fixed inset-0 bg-slate-900/50 z-50 flex items-center justify-center p-4" data-testid="bpm-invite-modal">
      <div className="bg-white rounded-lg shadow-xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-start justify-between p-5 border-b border-slate-200">
          <div className="flex items-center gap-2">
            <UserPlus className="w-5 h-5 text-sky-600" />
            <div>
              <h3 className="text-lg font-semibold text-slate-900">Invite Application Manager</h3>
              <p className="text-xs text-slate-500 mt-0.5">Send an OTP-based invite for an app you depend on</p>
            </div>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700" data-testid="bpm-invite-close-btn">
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={submit} className="p-5 space-y-4">
          {/* App selector */}
          <div>
            <label className="text-xs font-medium text-slate-700 block mb-1">
              Application <span className="text-rose-500">*</span>
            </label>
            {loading ? (
              <div className="flex items-center gap-2 text-sm text-slate-500 py-2">
                <Loader2 className="w-4 h-4 animate-spin" /> Loading eligible applications…
              </div>
            ) : apps.length === 0 ? (
              <p className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-md px-3 py-2">
                No applications are linked to your processes yet. Add an application as a dependency in one of your BIAs first.
              </p>
            ) : (
              <select
                value={form.application_id}
                onChange={(e) => setForm({ ...form, application_id: e.target.value })}
                className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-sky-500"
                required
                data-testid="bpm-invite-app-select"
              >
                <option value="">— Select an application —</option>
                {apps.map((a) => (
                  <option key={a.id} value={a.id}>
                    {a.application_name}{a.has_manager ? `  (current: ${a.current_manager || a.current_manager_email})` : "  (no manager)"}
                  </option>
                ))}
              </select>
            )}
            {selectedApp?.has_manager && (
              <p className="mt-1 text-xs text-amber-700">
                This app already has a manager. Inviting will replace them with the new invitee on acceptance.
              </p>
            )}
          </div>

          {/* Recipient details */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-medium text-slate-700 block mb-1">First name <span className="text-rose-500">*</span></label>
              <input
                type="text"
                required
                value={form.app_manager_first_name}
                onChange={(e) => setForm({ ...form, app_manager_first_name: e.target.value })}
                className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-sky-500"
                data-testid="bpm-invite-firstname-input"
              />
            </div>
            <div>
              <label className="text-xs font-medium text-slate-700 block mb-1">Last name</label>
              <input
                type="text"
                value={form.app_manager_last_name}
                onChange={(e) => setForm({ ...form, app_manager_last_name: e.target.value })}
                className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-sky-500"
                data-testid="bpm-invite-lastname-input"
              />
            </div>
          </div>

          <div>
            <label className="text-xs font-medium text-slate-700 block mb-1">Email <span className="text-rose-500">*</span></label>
            <input
              type="email"
              required
              value={form.app_manager_email}
              onChange={(e) => setForm({ ...form, app_manager_email: e.target.value })}
              placeholder="appmanager@company.com"
              className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-sky-500"
              data-testid="bpm-invite-email-input"
            />
          </div>

          <div>
            <label className="text-xs font-medium text-slate-700 block mb-1">Personal message (optional)</label>
            <textarea
              rows={3}
              value={form.message}
              onChange={(e) => setForm({ ...form, message: e.target.value })}
              placeholder="Hi — I'd like to invite you to manage IT-DRP for this app since it supports my BIA process…"
              className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-sky-500"
              data-testid="bpm-invite-message-input"
            />
          </div>

          {/* Footer */}
          <div className="flex items-center justify-between gap-3 pt-2 border-t border-slate-100">
            <p className="text-xs text-slate-500 flex items-center gap-1">
              <ShieldCheck className="w-3 h-3" /> 7-day OTP · Sent via email & in-app
            </p>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={onClose}
                className="px-3 py-1.5 text-sm text-slate-600 hover:text-slate-900"
                data-testid="bpm-invite-cancel-btn"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={submitting || !form.application_id}
                className="bg-sky-600 hover:bg-sky-700 disabled:opacity-50 text-white text-sm font-medium px-4 py-1.5 rounded-md flex items-center gap-2"
                data-testid="bpm-invite-submit-btn"
              >
                {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Mail className="w-4 h-4" />}
                {submitting ? "Sending…" : "Send invitation"}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}
